import React, { Fragment, useContext, memo, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import { ScoreSheetLineup, ScoreSheetAA } from '../shared/lineups';
import { ScoreSheetRounds } from '../shared/rounds';
import { ScoreSheetContext } from '../shared/context';
import { Clock } from '../shared/clock';
import { useEvalConfig } from '../../hooks';
import {
  getJudgeDisplayContext,
  coachData,
  formatAttendanceValue,
} from '../shared/utils';
import { convertMillipointsToDisplay } from '../../../../utilities/scoring';
import { editIcon } from '../../../helpers/icons';
import styles from '../../officialscoresheet.module.css';

const {
  header,
  row,
  teamName,
  teamNameLabel,
  teamNameTeam,
  matchDetailsShort,
  matchDetailsLong,
  matchDetailsLabel,
  matchDetailsShortField,
  matchDetailsLongField,
  matchDetailsEditButton,
  matchDetailsEditableField,
  editableField,
  editablePlaceholder,
  body,
  squadLeft,
  squadRight,
  footer,
  subFooter,
  signature,
  signatureLabel,
  signatureField,
  signatureName,
  signatureNames,
  timestamp,
  teamScore,
  teamScoreLabel,
  teamScoreValue,
  teamScoreValueEmpty,
  teamScoreSheet,
  reduceFont,
  headJudge,
} = styles;

function MensH2HHeader({
  teamA,
  teamB,
  format,
  date,
  timezone,
  location,
  attendance,
  onEditClick,
  editable,
  onLocationChange,
  onAttendanceChange,
}) {
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingAttendance, setIsEditingAttendance] = useState(false);
  const [localLocation, setLocalLocation] = useState(location);
  const [localAttendance, setLocalAttendance] = useState(attendance);

  const handleLocationClick = () => {
    if (editable) {
      setLocalLocation(location);
      setIsEditingLocation(true);
    }
  };

  const handleAttendanceClick = () => {
    if (editable) {
      setLocalAttendance(attendance);
      setIsEditingAttendance(true);
    }
  };

  const handleLocationBlur = () => {
    setIsEditingLocation(false);
    if (localLocation !== location) {
      onLocationChange?.(localLocation);
    }
  };

  const handleAttendanceBlur = () => {
    setIsEditingAttendance(false);
    if (localAttendance !== attendance) {
      onAttendanceChange?.(localAttendance);
    }
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditingLocation(false);
      if (localLocation !== location) {
        onLocationChange?.(localLocation);
      }
    } else if (e.key === 'Escape') {
      setIsEditingLocation(false);
      setLocalLocation(location);
    }
  };

  const handleAttendanceKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditingAttendance(false);
      if (localAttendance !== attendance) {
        onAttendanceChange?.(localAttendance);
      }
    } else if (e.key === 'Escape') {
      setIsEditingAttendance(false);
      setLocalAttendance(attendance);
    }
  };

  return (
    <section className={header}>
      <h1>{format.title}</h1>
      <Row className={row}>
        <Col className={teamName}>
          <div className={teamNameLabel}>Home: </div>
          <div className={teamNameTeam}>
            {(teamA && teamA?.forceTitle
              ? `${teamA?.altName}: ${teamA?.displayName}`
              : teamA?.altName) || ''}
            {teamA?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
        <Col className={teamName}>
          <div className={teamNameLabel}>Visitor: </div>
          <div className={teamNameTeam}>
            {(teamB && teamB?.forceTitle
              ? `${teamB?.altName}: ${teamB?.displayName}`
              : teamB?.altName) || ''}
            {teamB?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Date: </div>
          <div className={matchDetailsShortField}>{`${formatInTimeZone(
            new Date(date),
            timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "M'/'dd'/'yyyy"
          )}`}</div>
        </Col>
        <Col className={matchDetailsLong}>
          <div className={matchDetailsLabel}>Location: </div>
          {isEditingLocation ? (
            <input
              type="text"
              className={matchDetailsEditableField}
              value={localLocation}
              onChange={(e) => setLocalLocation(e.target.value)}
              onBlur={handleLocationBlur}
              onKeyDown={handleLocationKeyDown}
              autoFocus
              placeholder="Enter location..."
            />
          ) : (
            <>
              <div
                className={`${matchDetailsLongField} ${
                  editable ? editableField : ''
                }`}
                onClick={handleLocationClick}
                title={editable ? 'Click to edit' : undefined}
              >
                {location ||
                  (editable ? (
                    <span className={editablePlaceholder}>+ Add</span>
                  ) : (
                    ''
                  ))}
              </div>
              {!location && !editable && onEditClick ? (
                <Button
                  variant="light"
                  className={matchDetailsEditButton}
                  onClick={onEditClick}
                >
                  {editIcon}
                </Button>
              ) : null}
            </>
          )}
        </Col>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Attendance: </div>
          {isEditingAttendance ? (
            <input
              type="text"
              className={matchDetailsEditableField}
              value={localAttendance}
              onChange={(e) => setLocalAttendance(e.target.value)}
              onBlur={handleAttendanceBlur}
              onKeyDown={handleAttendanceKeyDown}
              autoFocus
              placeholder="Enter attendance..."
            />
          ) : (
            <>
              <div
                className={`${matchDetailsShortField} ${
                  editable ? editableField : ''
                }`}
                onClick={handleAttendanceClick}
                title={editable ? 'Click to edit' : undefined}
              >
                {formatAttendanceValue(attendance) ||
                  (editable ? (
                    <span className={editablePlaceholder}>+ Add</span>
                  ) : (
                    ''
                  ))}
              </div>
              {!attendance && !editable && onEditClick ? (
                <Button
                  variant="light"
                  className={matchDetailsEditButton}
                  onClick={onEditClick}
                >
                  {editIcon}
                </Button>
              ) : null}
            </>
          )}
        </Col>
      </Row>
    </section>
  );
}

function MensH2HBody({
  teamA,
  teamB,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
}) {
  const lineupMaxes = apparatusArray.map((a, i) => {
    if (teamA?.individuals || teamB?.individuals) {
      return Math.max(
        teamA?.events?.[i]?.lineup?.length || lineupMax,
        teamB?.events?.[i]?.lineup?.length || lineupMax,
        lineupMax
      );
    }
    return lineupMax;
  });

  return (
    <main className={body}>
      <Row className={row}>
        <Col className={squadLeft}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetLineup
                squad={teamA?.events?.[i]?.lineup}
                showSidebar={true}
                apparatus={a}
                max={lineupMaxes?.[i]}
                isIndividuals={teamA?.individuals}
                teamsByAthleteId={teamsByAthleteId}
              />
              <ScoreSheetRounds
                data={!teamA?.individuals ? teamA?.events?.[i] : null}
                apparatus={a}
              />
            </div>
          ))}
        </Col>
        <Col className={squadRight}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetLineup
                squad={teamB?.events?.[i]?.lineup}
                showSidebar={false}
                apparatus={a}
                max={lineupMaxes?.[i]}
                isIndividuals={teamB?.individuals}
                teamsByAthleteId={teamsByAthleteId}
              />
              <ScoreSheetRounds
                data={!teamB?.individuals ? teamB?.events?.[i] : null}
                apparatus={a}
              />
            </div>
          ))}
        </Col>
      </Row>
      <Row>
        <Col className={squadLeft}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!!teamA?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={
                !!teamA?.neutral ? teamScoreValue : teamScoreValueEmpty
              }
            >
              {!!teamA?.neutral
                ? convertMillipointsToDisplay(teamA.neutral, 1)
                : null}
            </Col>
            {!!teamA && (
              <>
                <Col className={teamScoreLabel}>Final Score: </Col>
                <Col className={teamScoreValue}>
                  {!teamA?.individuals ? teamA?.finalScore : null}
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col className={squadRight}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!!teamB?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={
                !!teamB?.neutral ? teamScoreValue : teamScoreValueEmpty
              }
            >
              {!!teamB?.neutral
                ? convertMillipointsToDisplay(teamB.neutral, 1)
                : null}
            </Col>
            {!!teamB && (
              <>
                <Col className={teamScoreLabel}>Final Score: </Col>
                <Col className={teamScoreValue}>
                  {!teamB?.individuals ? teamB?.finalScore : null}
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={squadLeft}>
          <ScoreSheetAA
            data={teamA?.aa}
            showSidebar={true}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
        <Col className={squadRight}>
          <ScoreSheetAA
            data={teamB?.aa}
            showSidebar={false}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
      </Row>
    </main>
  );
}

function MensH2HFooter({
  teamA,
  teamB,
  page,
  apparatusArray,
  judgesByApparatus,
  maxJudges,
  gender,
  session,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const { hideJudgeNames } = useContext(ScoreSheetContext);
  const evalConfig = useEvalConfig();
  const { dPanel, ePanel, jPanel } = evalConfig;

  const rankIt = (scoreTypes) => {
    const judgeRanking = [...dPanel, ...ePanel, ...jPanel];
    let rank = judgeRanking.length;

    for (let i = 0; i < judgeRanking.length; i++) {
      if (scoreTypes.includes(judgeRanking[i].type)) {
        rank = i;
        break;
      }
    }

    return rank;
  };

  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return (
    <section className={footer}>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature} style={{ display: 'block' }}>
            <h5>Signatures:</h5>
            <span className={headJudge}>* Head Judge</span>
          </Col>
        </Row>
        <Row className={row}>
          {apparatusArray.map((a, i) => (
            <Col key={i} className={signature}>
              <div className={signatureLabel}>
                {`${a}: `}
                <sup>*</sup>
              </div>
              <div className={signatureField}></div>
            </Col>
          ))}
        </Row>
        <Row className={row}>
          {apparatusArray.map((a, i) => {
            const headJudgeData = judgesByApparatus[a]?.headJudge;

            return (
              <Col key={i} className={signatureNames}>
                {!!headJudgeData && (
                  <>
                    <div className={signatureLabel}></div>
                    <div className={signatureName}>
                      {!hideJudgeNames
                        ? headJudgeData.sessionJudgeAssignment?.judge?.name
                        : ''}{' '}
                      {!hideJudgeNames
                        ? getJudgeDisplayContext(headJudgeData, gender)
                        : ''}
                    </div>
                  </>
                )}
              </Col>
            );
          })}
        </Row>

        {Array(maxJudges || 1)
          .fill('a')
          .map((_, index) => {
            return (
              <Fragment key={index}>
                <Row key={index} className={row}>
                  {apparatusArray.map((a, i) => (
                    <Col key={i} className={signature}>
                      <div className={signatureLabel}>{`${a}: `}</div>
                      <div className={signatureField}></div>
                    </Col>
                  ))}
                </Row>
                <Row className={row}>
                  {apparatusArray.map((a, i) => {
                    const judge = Object.values(
                      judgesByApparatus[a]?.rest || {}
                    ).sort((a, b) => {
                      if (rankIt(a.scoreTypes) < rankIt(b.scoreTypes)) {
                        return -1;
                      }
                      if (rankIt(a.scoreTypes) > rankIt(b.scoreTypes)) {
                        return 1;
                      }
                      return 0;
                    })[index];
                    const judgeName =
                      !!judge && judge.sessionJudgeAssignment?.judge?.name;
                    const judgeQual =
                      !!judge && getJudgeDisplayContext(judge, gender);

                    return (
                      <Col key={i} className={signatureNames}>
                        <div className={signatureLabel}></div>
                        <div className={signatureName}>
                          {!hideJudgeNames ? judgeName : ''}{' '}
                          {!hideJudgeNames ? judgeQual : ''}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Fragment>
            );
          })}
      </section>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature}>
            <div className={signatureLabel}>{`Coach: `}</div>
            <div className={signatureField}></div>
          </Col>
          <Col className={signature}>
            <div className={signatureLabel}>{`Coach: `}</div>
            <div className={signatureField}></div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
            <div className={signatureName}>
              {!!teamA?.name
                ? `${
                    coachData?.[session.gender]?.[teamA?.rtnId]?.headCoach ??
                    coachData['C1'].name
                  } (${
                    teamA?.forceTitle
                      ? `${teamA?.altName}: ${teamA?.displayName}`
                      : teamA?.altName
                  })`
                : null}
            </div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>

            <div className={signatureName}>
              {!!teamB?.name
                ? `${
                    coachData?.[session.gender]?.[teamB?.rtnId]?.headCoach ??
                    coachData['C2'].name
                  } (${
                    teamB?.forceTitle
                      ? `${teamB?.altName}: ${teamB?.displayName}`
                      : teamB?.altName
                  })`
                : null}
            </div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel} />
            <div className={signatureName}>
              {teamA?.conductCard ? `Yellow Card: ${teamA?.conductCard}` : null}
            </div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel} />
            <div className={signatureName}>
              {teamB?.conductCard ? `Yellow Card: ${teamB?.conductCard}` : null}
            </div>
          </Col>
        </Row>
      </section>
      {copyrightTimePageFooter(page)}
    </section>
  );
}

export const MensH2HScoresheet = memo(_MensH2HScoresheet);

function _MensH2HScoresheet({
  options,
  page,
  teams,
  teamAIndex: customTeamAIndex,
  teamBIndex: customTeamBIndex,
  format,
  date,
  timezone,
  location,
  attendance,
  session,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
  judgesByApparatus,
  maxJudges,
  singleSheetOnly,
  sheetOptionsLength,
  onEditClick,
  editable = false,
  onLocationChange,
  onAttendanceChange,
}) {
  const evalConfig = useEvalConfig();
  const { ePanel, jPanel } = evalConfig;
  const isReduceFont =
    Math.max(jPanel?.length || 0, ePanel?.length || 0) > 4 ? reduceFont : null;

  const indices = (options && options.split('_')) || '';
  let aIndex = indices.length > 0 ? parseInt(indices[0]) ?? null : null;
  let bIndex = indices.length > 1 ? parseInt(indices[1]) ?? null : null;

  // Override with custom settings
  if (customTeamAIndex !== null && customTeamAIndex !== undefined) {
    aIndex = customTeamAIndex;
  }

  if (customTeamBIndex !== null && customTeamBIndex !== undefined) {
    bIndex = customTeamBIndex;
  }

  const teamA = aIndex < teams.length ? teams[aIndex] : null;
  const teamB = bIndex < teams.length ? teams[bIndex] : null;

  return (
    <div className={[teamScoreSheet, isReduceFont].filter(Boolean).join(' ')}>
      <MensH2HHeader
        teamA={teamA}
        teamB={teamB}
        format={format}
        date={date}
        timezone={timezone}
        location={location}
        attendance={attendance}
        onEditClick={onEditClick}
        editable={editable}
        onLocationChange={onLocationChange}
        onAttendanceChange={onAttendanceChange}
      />
      <MensH2HBody
        teamA={teamA}
        teamB={teamB}
        apparatusArray={apparatusArray}
        lineupMax={lineupMax}
        teamsByAthleteId={teamsByAthleteId}
      />
      <MensH2HFooter
        teamA={teamA}
        teamB={teamB}
        page={page}
        apparatusArray={apparatusArray}
        judgesByApparatus={judgesByApparatus}
        maxJudges={maxJudges}
        gender={session.gender}
        session={session}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </div>
  );
}
